/**
 * PraticaDetailPage — F-005 Phase 4 (IMPL-P4-006).
 *
 * Pagina di dettaglio pratica per il cliente. Mostra:
 *   - heading con `codicePratica` + `StateBadge` + `HeartbeatLabel`
 *   - se BLOCCATA: `ErrorSegnaloBanner` con CTA correzione
 *   - `PraticaSummaryCard`: campi chiave dei datiStrutturati
 *   - `PraticaTimeline`: cronologia eventi (cliente + operatore)
 *
 * Polling: refetch ogni 15s tramite `usePraticaDetail` (vedi hook).
 */
import { useParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { HeartbeatLabel } from '@/components/HeartbeatLabel';
import type { SegnalazioneReason } from '@app/shared';
import { SupportFloatingWidget } from '@/features/support';
import { usePraticaDetail } from '../hooks/usePraticaDetail';
import { PraticaSummaryCard } from '../components/PraticaSummaryCard';
import { PraticaTimeline } from '../components/PraticaTimeline';
import { ErrorSegnaloBanner } from '../components/ErrorSegnaloBanner';
import { StateBadge } from '../components/StateBadge';
import { AdempimentoCTA } from '../components/AdempimentoCTA';

export function PraticaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isPending, isError, dataUpdatedAt } = usePraticaDetail(id ?? '');

  if (isPending) {
    return (
      <AppLayout>
        <p
          role="status"
          aria-live="polite"
          className="py-8 text-center text-sm text-muted-foreground"
        >
          Caricamento pratica…
        </p>
      </AppLayout>
    );
  }

  if (isError || !data) {
    return (
      <AppLayout>
        <p
          role="alert"
          className="py-8 text-center text-sm text-destructive"
        >
          Pratica non trovata.
        </p>
      </AppLayout>
    );
  }

  const { pratica, timeline } = data;

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold tracking-tight">
              Pratica <span className="font-mono">{pratica.codicePratica}</span>
            </h1>
            <StateBadge stato={pratica.stato} />
          </div>
          <div className="flex items-center gap-3">
            {/* F-004: CTA "Registra adempimento" — nascosta (non disabilitata)
                se la pratica non è COMPLETATA (Key Decision: l'azione ha senso
                solo su contratti completati). */}
            {pratica.stato === 'COMPLETATA' && <AdempimentoCTA praticaId={pratica.id} />}
            <HeartbeatLabel dataUpdatedAt={dataUpdatedAt} />
          </div>
        </div>

        {pratica.stato === 'BLOCCATA' && (
          <ErrorSegnaloBanner
            praticaId={pratica.id}
            blockedReasons={pratica.blockedReasons as SegnalazioneReason[]}
            blockedDetail={pratica.blockedDetail}
          />
        )}

        <PraticaSummaryCard pratica={pratica} />
        <PraticaTimeline events={timeline} />
      </div>
      <SupportFloatingWidget praticaId={pratica.id} />
    </AppLayout>
  );
}
